import { useState, useRef, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import "./ContactSection.css";

const API_URL = import.meta.env.VITE_API_URL ?? "";

// Google reCAPTCHA site key (получить на https://www.google.com/recaptcha/admin)
// Для разработки можно использовать тестовый ключ Google
const RECAPTCHA_SITE_KEY = "6Ld1tQgtAAAAABh4UqzhOHaWfaLXUa2LD4RrK0Lr";

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const OBJECT_TYPES = [
  { value: "production", label: "Производственный объект" },
  { value: "office",     label: "Офис / Бизнес-центр" },
  { value: "warehouse",  label: "Складской комплекс" },
  { value: "commercial", label: "Торговое помещение" },
  { value: "industrial", label: "Промышленное предприятие" },
  { value: "telecom",    label: "Сеть связи / серверная / ЦОД" },
  { value: "other",      label: "Другое" },
];

export default function ContactSection() {
  const [form, setForm] = useState({ 
    name: "", 
    phone: "", 
    objectType: "", 
    comment: "" 
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const recaptchaRef = useRef(null);

  // Таймер для кулдауна
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.startsWith("7") || value.startsWith("8")) {
      value = value.substring(1);
    }
    let formatted = "+7";
    if (value.length > 0) formatted += " (" + value.substring(0, 3);
    if (value.length > 3) formatted += ") " + value.substring(3, 6);
    if (value.length > 6) formatted += "-" + value.substring(6, 8);
    if (value.length > 8) formatted += "-" + value.substring(8, 10);
    setForm({ ...form, phone: formatted });
  };

  const handleCaptchaChange = (token) => {
    console.log("Captcha token received:", token ? "Yes" : "No");
    setCaptchaToken(token);
    if (error === "Пожалуйста, подтвердите, что вы не робот") {
      setError("");
    }
  };

  const resetForm = () => {
    setForm({ name: "", phone: "", objectType: "", comment: "" });
    setCaptchaToken(null);
    recaptchaRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Проверка кулдауна
    if (cooldown > 0) {
      setError(`Пожалуйста, подождите ${cooldown} секунд перед отправкой следующей заявки`);
      return;
    }
    
    // Проверка капчи
    if (!captchaToken) {
      setError("Пожалуйста, подтвердите, что вы не робот");
      return;
    }
    
    setLoading(true);
    setError("");

    try {
      const requestBody = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        object_type: form.objectType || null,
        comment: form.comment?.trim() || null,
        captcha_token: captchaToken,
      };
      
      console.log("Sending request to:", `${API_URL}/api/v1/leads/`);
      
      const res = await fetch(`${API_URL}/api/v1/leads/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Обработка rate limit (429)
        if (res.status === 429) {
          const match = data.detail?.match(/(\d+)/);
          const seconds = match ? parseInt(match[1]) : 60;
          setCooldown(seconds);
          throw new Error(data.detail || `Слишком много заявок. Попробуйте через ${seconds} секунд.`);
        }
        
        // Ошибка валидации
        if (res.status === 422) {
          console.error("Validation error details:", data);
          throw new Error("Пожалуйста, проверьте правильность заполнения формы.");
        }
        
        // Ошибка капчи
        if (res.status === 400 && data.detail?.includes("капчи")) {
          setCaptchaToken(null);
          recaptchaRef.current?.reset();
          throw new Error(data.detail);
        }
        
        throw new Error(data.detail || "Ошибка при отправке заявки. Попробуйте позже.");
      }

      // Успешная отправка
      setSubmitted(true);
      resetForm();
      
      // Автоматически скрываем сообщение об успехе через 5 секунд
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
      
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.message || "Не удалось отправить заявку. Попробуйте позже.");
      // Сбрасываем капчу только если ошибка связана с ней
      if (err.message?.toLowerCase().includes("капч")) {
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="contact__inner">
        <div className="contact__grid">

          {/* Левая колонка — информация */}
          <div className="contact__info">
            <p className="contact__label">Свяжитесь с нами</p>
            <h2 className="contact__title">
              Рассчитаем<br />ваш проект
            </h2>
            <p className="contact__desc">
              Уточним задачу, состав объекта, требования к СКС, СОТ, СКУД,
              серверной или сети связи и предложим следующий инженерный шаг:
              обследование, смету, проект или коммерческое предложение.
            </p>

            <div className="contact__contacts">
              <div className="contact__contact-row">
                <span className="contact__contact-label">Телефон</span>
                <a href="tel:+78003002910" className="contact__contact-value contact__contact-value--link">
                  8-800-300-29-10
                </a>
              </div>
              <div className="contact__contact-row">
                <span className="contact__contact-label">Email</span>
                <a href="mailto:sales@itsystem.ru" className="contact__contact-value contact__contact-value--link">
                  sales@itsystem.ru
                </a>
              </div>
              <div className="contact__contact-row">
                <span className="contact__contact-label">Режим работы</span>
                <span className="contact__contact-value">Пн-Пт: 9:00 – 18:00 | Сб-Вс: по записи</span>
              </div>
              <div className="contact__contact-row">
                <span className="contact__contact-label">География</span>
                <span className="contact__contact-value">Екатеринбург и весь УрФО</span>
              </div>
            </div>
          </div>

          {/* Правая колонка — форма */}
          <div className="contact__form-wrap">
            {submitted ? (
              <div className="contact__success">
                <CheckIcon />
                <h3 className="contact__success-title">Спасибо за заявку!</h3>
                <p className="contact__success-text">Мы свяжемся с вами в ближайшее время</p>
                <p className="contact__success-hint">
                  Наш специалист свяжется с вами в течение рабочего дня
                </p>
              </div>
            ) : (
              <form className="contact__form" onSubmit={handleSubmit} noValidate>

                <div className="contact__field">
                  <label htmlFor="name" className="contact__field-label">Ваше имя *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Иван"
                    className="contact__input"
                    disabled={loading}
                  />
                </div>

                <div className="contact__field">
                  <label htmlFor="phone" className="contact__field-label">Телефон *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handlePhoneChange}
                    required
                    placeholder="+7 (___) ___-__-__"
                    className="contact__input"
                    disabled={loading}
                  />
                </div>

                <div className="contact__field">
                  <label htmlFor="objectType" className="contact__field-label">Тип объекта</label>
                  <div className="contact__select-wrapper">
                    <select
                      id="objectType"
                      name="objectType"
                      value={form.objectType}
                      onChange={handleChange}
                      className="contact__select"
                      disabled={loading}
                    >
                      <option value="">Выберите тип объекта</option>
                      {OBJECT_TYPES.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <span className="contact__select-arrow">▼</span>
                  </div>
                </div>

                <div className="contact__field">
                  <label htmlFor="comment" className="contact__field-label">Комментарий</label>
                  <textarea
                    id="comment"
                    name="comment"
                    value={form.comment}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Например: СКС, видеонаблюдение, СКУД, серверная, Wi-Fi, ВОЛС..."
                    className="contact__textarea"
                    disabled={loading}
                  />
                </div>

                {/* Google reCAPTCHA */}
                <div className="contact__field contact__captcha">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    hl="ru"
                    theme="light"
                    size="normal"
                  />
                </div>

                {/* Индикатор кулдауна */}
                {cooldown > 0 && (
                  <div className="contact__cooldown">
                    <span className="contact__cooldown-icon">⏳</span>
                    <span>Подождите {cooldown} секунд перед следующей отправкой</span>
                  </div>
                )}

                <div className="contact__submit-wrap">
                  {error && (
                    <div className="contact__error">
                      <span className="contact__error-icon">⚠️</span>
                      <span>{error}</span>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    className="contact__submit" 
                    disabled={loading || cooldown > 0 || !captchaToken}
                  >
                    {loading ? (
                      <>
                        <span className="contact__spinner"></span>
                        <span>Отправляем…</span>
                      </>
                    ) : (
                      <>
                        <span>Отправить задачу инженеру</span>
                        <ArrowRightIcon />
                      </>
                    )}
                  </button>
                  
                  <p className="contact__privacy">
                    Нажимая кнопку, вы соглашаетесь с 
                    <a href="/privacy" target="_blank" rel="noopener noreferrer"> политикой конфиденциальности</a>
                  </p>
                </div>

              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}